import Text from 'src/components/Text/Text';
import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import InlineLoadingBar from '../InlineLoadingBar/InlineLoadingBar';
import { SPOT_MARKETS_LOOKUP } from 'src/environmentVariables/EnvironmentVariables';
import { ButtonGroup } from '../Button';
import ColourCodedValue from '../Utils/ColourCodedValue';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import UI_UTILS from 'src/utils/uiUtils';
import {
	PerformanceDataSet,
	DEFAULT_PERFORMANCE_DATA,
	PnLTimeSeriesDataPoint,
	PERFORMANCE_TIMEFRAMES,
	PnlTimePeriodOption,
} from 'src/@types/types';
import dynamic from 'next/dynamic';

const PnlChart = dynamic(() => import('../PnlChart'));

const DEFAULT_INDEX = 2;

const IFPerformanceModal = () => {
	const setState = useDriftStore((s) => s.set);
	const currentlySelectedIF = useDriftStore((s) => s.selectedIfStakeMarket);
	const marketIndex = currentlySelectedIF.marketIndex;
	const displayDecimals = 5;
	const [allPerformanceData, setAllPerformanceData] =
		useState<PerformanceDataSet>(DEFAULT_PERFORMANCE_DATA);
	const [chartData, setChartData] = useState<PnLTimeSeriesDataPoint[]>();
	const [rawDiff, setRawDiff] = useState(0);
	const [pctDiff, setPctDiff] = useState(0);

	const [selectedTimeFrame, setSelectedTimeFrame] = useState(
		PERFORMANCE_TIMEFRAMES[DEFAULT_INDEX]
	);

	const onClose = useCallback(() => {
		setState((s) => {
			s.modals.showIfPerformanceModal = false;
		});
	}, []);

	const getAndSetData = () => {
		ExchangeHistoryClient.getInsuranceFundRecords(marketIndex).then(
			(result) => {
				if (result.success) {
					const allDataPoints = result?.body.data.insuranceFundRecords
						.filter((record) => !record.totalIfSharesAfter.eqZero())
						.map((record) => {
							return {
								date: new Date(record.ts.toNumber() * 1000),
								totalPnl: record.insuranceVaultAmountBefore
									.add(record.amount)
									// shares can be extremely small
									.scale(1000000, record.totalIfSharesAfter.toNum() * 1000000)
									.toNum(),
							};
						});
					const points90d = allDataPoints;
					const points30d = UI_UTILS.applyTimeFilter(allDataPoints, '30d');
					const points7d = UI_UTILS.applyTimeFilter(allDataPoints, '7d');
					const points24h = UI_UTILS.applyTimeFilter(allDataPoints, '24h');
					setAllPerformanceData({
						'24h': points24h,
						'7d': points7d,
						'30d': points30d,
						all: points90d,
					});
				}
			}
		);
	};

	useEffect(() => {
		setSelectedTimeFrame(PERFORMANCE_TIMEFRAMES[DEFAULT_INDEX]);
		getAndSetData();
	}, [marketIndex]);

	useEffect(() => {
		if (
			allPerformanceData[selectedTimeFrame] &&
			allPerformanceData[selectedTimeFrame].length > 0
		) {
			setChartData(allPerformanceData[selectedTimeFrame]);

			const diff =
				allPerformanceData[selectedTimeFrame][
					allPerformanceData[selectedTimeFrame].length - 1
				].totalPnl - allPerformanceData[selectedTimeFrame][0].totalPnl;
			setRawDiff(diff);
			setPctDiff(diff / allPerformanceData[selectedTimeFrame][0].totalPnl);
		}
	}, [allPerformanceData, selectedTimeFrame]);

	const chartLabel = 'IF Share Price';

	return (
		<Modal
			onClose={onClose}
			id={`if_performance_modal_${marketIndex}`}
			sizeConfig={{ xs: 16, sm: 12, md: 8, xl: 6 }}
			className="p-4"
		>
			<Modal.Body>
				<Modal.Header
					showX
					onClose={onClose}
					showBack={false}
					borderBottom={false}
				>
					<Text.H2>
						{`${SPOT_MARKETS_LOOKUP[marketIndex].symbol} IF Share Price: Historical Returns`}
					</Text.H2>
				</Modal.Header>

				<div className="items-center justify-start flex-grow w-full px-4 pb-2">
					<Text.H1>
						<span className="pl-2 text-2xl whitespace-nowrap">
							{chartData?.length ? (
								<ColourCodedValue className="inline-flex" value={rawDiff}>
									{`${pctDiff >= 0 ? '+' : '-'}${parseFloat(
										(pctDiff * 100).toFixed(2)
									)}%`}
								</ColourCodedValue>
							) : (
								<SkeletonValuePlaceholder
									className="w-12 h-6"
									loading={false}
								/>
							)}
						</span>
					</Text.H1>
				</div>

				<div className="items-center justify-center flex-grow min-h-[430px] max-h-[430px] w-full px-4 pb-4 pt-1">
					{!chartData ? (
						<div
							className={`flex flex-col justify-center items-center h-[430px] w-full text-center text-th-fgd-3 rounded-md`}
						>
							<InlineLoadingBar />
						</div>
					) : (
						<PnlChart
							id="if-performance"
							data={chartData}
							field={'totalPnl'}
							label={chartLabel}
							positive={false}
							decimalsToShow={displayDecimals}
							isInModal={true}
							overrideNoBeginAtZero={true}
							className="min-h-[430px]"
							isHourly={selectedTimeFrame === '24h'}
						/>
					)}
				</div>
				<div className="inline-flex items-center justify-between w-full">
					<div>
						<ButtonGroup.Segmented
							className="m-4"
							options={PERFORMANCE_TIMEFRAMES.map((label) => {
								return {
									label: label === 'all' ? '90d' : label,
									value: label,
								};
							})}
							selected={selectedTimeFrame}
							size="MEDIUM"
							selectAction={(val) =>
								setSelectedTimeFrame(val as PnlTimePeriodOption)
							}
						/>
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default IFPerformanceModal;
